/**
 * AuthHealth — owner-only dashboard surfacing sign-in failures over the last
 * 24h / 7d. Shows aggregate counts, breakdown by failure reason, and any
 * "stuck" users who failed 3+ times in 24h.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, AlertTriangle, RefreshCw, Loader2, Mail, ExternalLink, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthHealth() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [smokeBusy, setSmokeBusy] = useState(false);
    const [smoke, setSmoke] = useState(null);
    const [reissueBusy, setReissueBusy] = useState(false);
    const [reissueEmail, setReissueEmail] = useState("");
    const [reissueResult, setReissueResult] = useState(null);

    const load = async () => {
        setBusy(true);
        try {
            const r = await axios.get(`${API}/coach/auth-health`, { withCredentials: true });
            setData(r.data);
        } catch (e) {
            toast.error("Failed to load auth health", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { load(); }, []);

    const runSmoke = async () => {
        setSmokeBusy(true);
        setSmoke(null);
        try {
            const r = await axios.get(`${API}/health/auth/smoke`);
            setSmoke(r.data);
            if (r.data.ok) toast.success(`Smoke passed in ${r.data.total_ms}ms`);
            else toast.error("Smoke FAILED", { description: r.data.error });
        } catch (e) {
            toast.error("Smoke endpoint unreachable");
        } finally {
            setSmokeBusy(false);
        }
    };

    const reissue = async (e) => {
        e?.preventDefault?.();
        if (!reissueEmail) return;
        setReissueBusy(true);
        setReissueResult(null);
        try {
            const r = await axios.post(
                `${API}/coach/auth/force-reissue`,
                { email: reissueEmail },
                { withCredentials: true },
            );
            setReissueResult(r.data);
            toast.success(`Restore link minted for ${reissueEmail}`);
        } catch (e) {
            toast.error("Could not mint restore link", { description: e?.response?.data?.detail });
        } finally {
            setReissueBusy(false);
        }
    };

    if (user?.role !== "owner") {
        return <div data-testid="auth-health-denied" className="p-10 text-center text-zinc-400">Owner access only.</div>;
    }

    return (
        <div data-testid="auth-health-page" className="min-h-screen bg-brand-obsidian text-white">
            <div className="max-w-5xl mx-auto px-6 py-8">
                <Link to="/coach" className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 hover:text-white inline-flex items-center gap-1.5">
                    <ArrowLeft className="w-3 h-3" /> Coach console
                </Link>

                <div className="flex items-end justify-between mt-3 mb-6 gap-4 flex-wrap">
                    <div>
                        <h1 className="font-display text-4xl tracking-wider text-white mb-1 flex items-center gap-3">
                            <ShieldCheck className="w-7 h-7 text-emerald-400" /> Auth Health
                        </h1>
                        <p className="text-sm text-zinc-300">
                            Sign-in failures over the last 24h / 7d. If anyone is stuck, they show up here before they message you.
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid="auth-health-refresh"
                        onClick={load}
                        disabled={busy}
                        className="btn-ghost-tactical text-xs inline-flex items-center gap-2"
                    >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
                    </button>
                </div>

                {busy && !data && (
                    <div className="text-center py-20"><Loader2 className="w-7 h-7 text-brand-red animate-spin mx-auto" /></div>
                )}

                {data && (
                    <>
                        {/* Stats grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                            <Stat label="Failures 24h" value={data.totals.failures_24h} tone={data.totals.failures_24h > 0 ? "warn" : "ok"} testid="stat-failures-24h" />
                            <Stat label="Failures 7d" value={data.totals.failures_7d} tone={data.totals.failures_7d > 5 ? "warn" : "ok"} testid="stat-failures-7d" />
                            <Stat label="Sign-ins 24h" value={data.totals.successes_24h} tone="info" testid="stat-successes-24h" />
                            <Stat label="Sign-ins 7d" value={data.totals.successes_7d} tone="info" testid="stat-successes-7d" />
                        </div>

                        {/* Smoke test button */}
                        <div className="border border-zinc-800 bg-zinc-950 rounded-lg p-5 mb-6">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                    <div className="text-[10px] tracking-[0.25em] uppercase text-emerald-400 mb-1">End-to-end smoke test</div>
                                    <p className="text-sm text-zinc-300">Mints a synthetic magic-link token, verifies it, fetches the user, and cleans up. Runs against the real database.</p>
                                </div>
                                <button
                                    type="button"
                                    data-testid="auth-health-run-smoke"
                                    onClick={runSmoke}
                                    disabled={smokeBusy}
                                    className="btn-tactical text-xs inline-flex items-center gap-2"
                                >
                                    {smokeBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />} Run smoke now
                                </button>
                            </div>
                            {smoke && (
                                <div data-testid="auth-health-smoke-result" className={`mt-4 text-xs rounded-md p-3 ${smoke.ok ? "bg-emerald-950/30 border border-emerald-800 text-emerald-200" : "bg-red-950/30 border border-red-800 text-red-200"}`}>
                                    {smoke.ok ? <CheckCircle2 className="w-4 h-4 inline mr-1.5" /> : <AlertTriangle className="w-4 h-4 inline mr-1.5" />}
                                    <strong>{smoke.ok ? "PASS" : "FAIL"}</strong> · {smoke.total_ms}ms total
                                    {smoke.error && <div className="mt-1 text-[11px]">Error: {smoke.error}</div>}
                                    <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
                                        {smoke.steps.map((s, i) => (
                                            <div key={i} className={s.ok === false ? "text-red-300" : "text-zinc-300"}>
                                                {s.step}: <strong>{s.ms ?? "—"}ms</strong>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Force re-issue tool */}
                        <div className="border border-amber-900/50 bg-amber-950/10 rounded-lg p-5 mb-6">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-amber-300 mb-1">Recovery — force re-issue session</div>
                            <p className="text-sm text-zinc-300 mb-3">Mints a one-time URL you can paste to a user who's stuck signing in. Bypasses Google + magic-link entirely.</p>
                            <form onSubmit={reissue} className="flex gap-2 flex-wrap">
                                <input
                                    type="email"
                                    required
                                    value={reissueEmail}
                                    onChange={(e) => setReissueEmail(e.target.value)}
                                    placeholder="email@example.com"
                                    data-testid="auth-reissue-email"
                                    className="flex-1 min-w-[200px] bg-black border border-zinc-800 px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-amber-500"
                                />
                                <button
                                    type="submit"
                                    disabled={reissueBusy || !reissueEmail}
                                    data-testid="auth-reissue-submit"
                                    className="btn-tactical text-xs inline-flex items-center gap-2 disabled:opacity-50"
                                >
                                    {reissueBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Mint restore link
                                </button>
                            </form>
                            {reissueResult && (
                                <div data-testid="auth-reissue-result" className="mt-3 p-3 bg-black/60 border border-amber-700/40 rounded text-xs">
                                    <div className="text-amber-200 mb-1">For <strong>{reissueResult.user.email}</strong> ({reissueResult.user.name}) — role: {reissueResult.user.role || "client"}</div>
                                    <div className="font-mono text-emerald-300 break-all">{reissueResult.restore_url}</div>
                                    <button
                                        type="button"
                                        data-testid="auth-reissue-copy"
                                        onClick={() => { navigator.clipboard.writeText(reissueResult.restore_url); toast.success("Copied"); }}
                                        className="mt-2 text-[10px] tracking-[0.25em] uppercase text-amber-300 hover:text-white"
                                    >
                                        Copy URL · expires in {reissueResult.expires_in_minutes}m, one-time use
                                    </button>
                                </div>
                            )}
                        </div>

                        <WaiverPopupAudit />

                        {/* Stuck users */}
                        {data.stuck_users_24h.length > 0 && (
                            <div className="mb-6">
                                <h2 className="text-sm font-bold text-red-300 uppercase tracking-wider mb-3">
                                    <AlertTriangle className="w-4 h-4 inline mr-2" />Users stuck in the last 24h
                                </h2>
                                <div className="space-y-2">
                                    {data.stuck_users_24h.map((s) => (
                                        <div key={s.email} data-testid={`stuck-${s.email}`} className="border border-red-900/50 bg-red-950/20 rounded p-3 flex items-center justify-between gap-3">
                                            <div>
                                                <div className="text-white font-bold">{s.email}</div>
                                                <div className="text-[11px] text-red-300">{s.count} failed attempts</div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => { setReissueEmail(s.email); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                                                className="text-[10px] tracking-[0.2em] uppercase text-amber-300 hover:text-white"
                                            >
                                                Re-issue session →
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Breakdown by reason */}
                        {Object.keys(data.by_reason_24h).length > 0 && (
                            <div className="mb-6">
                                <h2 className="text-sm font-bold text-zinc-300 uppercase tracking-wider mb-3">Failure reasons (24h)</h2>
                                <div className="grid sm:grid-cols-2 gap-2">
                                    {Object.entries(data.by_reason_24h).map(([reason, count]) => (
                                        <div key={reason} className="border border-zinc-800 bg-zinc-950 rounded p-3 flex justify-between">
                                            <span className="text-zinc-300 text-sm">{reason}</span>
                                            <span className="text-white font-bold">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Recent failures log */}
                        <div>
                            <h2 className="text-sm font-bold text-zinc-300 uppercase tracking-wider mb-3">Recent failures (last 7d)</h2>
                            {data.recent_failures.length === 0 ? (
                                <div className="text-zinc-500 text-sm italic py-6 text-center border border-zinc-800 rounded">No failures in the last 7 days. </div>
                            ) : (
                                <div className="space-y-2">
                                    {data.recent_failures.slice(0, 30).map((f, i) => (
                                        <div key={i} data-testid={`failure-row-${i}`} className="border border-zinc-800 bg-zinc-950 rounded p-3 text-xs">
                                            <div className="flex items-center justify-between flex-wrap gap-2">
                                                <div>
                                                    <span className="text-white font-mono">{f.method}</span>
                                                    <span className="text-zinc-500"> · </span>
                                                    <span className="text-red-300">{f.reason}</span>
                                                    {f.email && <><span className="text-zinc-500"> · </span><span className="text-zinc-300">{f.email}</span></>}
                                                </div>
                                                <span className="text-[10px] text-zinc-500">{new Date(f.occurred_at).toLocaleString()}</span>
                                            </div>
                                            {f.detail && <div className="mt-1 text-zinc-400 text-[11px]">{f.detail}</div>}
                                            <div className="text-[10px] text-zinc-600 mt-1">IP: {f.ip || "—"} · status: {f.status_code}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <p className="text-[10px] text-zinc-500 mt-8 inline-flex items-center gap-1.5">
                            <ExternalLink className="w-3 h-3" /> Set up a synthetic monitor on <code className="text-emerald-300">/api/health/auth/smoke</code> for 5-minute heartbeat alerts.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}

function WaiverPopupAudit() {
    const [busy, setBusy] = useState(false);
    const [healBusy, setHealBusy] = useState(false);
    const [audit, setAudit] = useState(null);

    const run = async () => {
        setBusy(true);
        try {
            const r = await axios.get(`${API}/coach/waiver/popup-audit`, { withCredentials: true });
            setAudit(r.data);
        } catch (e) {
            toast.error("Audit failed", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    const heal = async () => {
        setHealBusy(true);
        try {
            const r = await axios.post(`${API}/coach/waiver/popup-audit/heal`, {}, { withCredentials: true });
            toast.success(`Healed ${r.data.healed} accounts`, { description: `${r.data.remaining_truly_unsigned} still have no waiver on record anywhere.` });
            await run();
        } catch (e) {
            toast.error("Heal failed", { description: e?.response?.data?.detail });
        } finally {
            setHealBusy(false);
        }
    };

    return (
        <div data-testid="waiver-popup-audit" className="border border-zinc-800 bg-zinc-950 rounded-lg p-5 mb-6">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-1">Waiver popup audit</div>
            <p className="text-sm text-zinc-300 mb-3">Lists every client who would see the "Sign the waiver" popup at login, and whether we have waiver evidence (kid's waiver / assessment booking) that can auto-clear it.</p>
            <div className="flex gap-2 flex-wrap">
                <button type="button" onClick={run} disabled={busy} data-testid="waiver-audit-run" className="btn-tactical text-xs inline-flex items-center gap-2 disabled:opacity-50">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Run audit
                </button>
                {audit && audit.healable_count > 0 && (
                    <button type="button" onClick={heal} disabled={healBusy} data-testid="waiver-audit-heal" className="text-xs inline-flex items-center gap-2 border border-emerald-700 text-emerald-300 px-3 py-2 rounded hover:bg-emerald-950/40 disabled:opacity-50">
                        {healBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Heal {audit.healable_count} with evidence
                    </button>
                )}
            </div>
            {audit && (
                <div className="mt-4" data-testid="waiver-audit-results">
                    <div className="text-xs text-zinc-400 mb-2">
                        <span className="text-white font-bold">{audit.affected_count}</span> would see the popup · <span className="text-emerald-300 font-bold">{audit.healable_count}</span> auto-healable · <span className="text-amber-300 font-bold">{audit.truly_unsigned_count}</span> truly unsigned
                    </div>
                    {audit.rows.length === 0 ? (
                        <div className="text-emerald-300 text-sm py-3 text-center border border-emerald-900/50 rounded">Nobody sees the popup. All clear.</div>
                    ) : (
                        <div className="space-y-1.5 max-h-80 overflow-y-auto">
                            {audit.rows.map((r) => (
                                <div key={r.user_id} data-testid={`waiver-audit-row-${r.user_id}`} className="border border-zinc-800 bg-black rounded p-2.5 text-xs flex items-center justify-between gap-3 flex-wrap">
                                    <div>
                                        <span className="text-white">{r.name || "—"}</span>
                                        <span className="text-zinc-500"> · </span>
                                        <span className="text-zinc-300">{r.email}</span>
                                        {r.status && <span className="text-zinc-600"> · {r.status}</span>}
                                    </div>
                                    <span className={r.healable ? "text-emerald-300" : "text-amber-300"}>
                                        {r.athlete_waiver_evidence ? "kid's waiver on file" : r.assessment_waiver_evidence ? "assessment waiver on file" : "no waiver anywhere"}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, tone, testid }) {
    const ring = tone === "warn" ? "border-amber-700" : tone === "info" ? "border-zinc-800" : "border-emerald-800";
    const num = tone === "warn" ? "text-amber-300" : tone === "info" ? "text-white" : "text-emerald-300";
    return (
        <div data-testid={testid} className={`border ${ring} bg-zinc-950 rounded-lg p-4`}>
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-1">{label}</div>
            <div className={`text-3xl font-bold ${num}`}>{value}</div>
        </div>
    );
}
